// @ts-nocheck
import { GridPageChangeEvent } from '@progress/kendo-react-grid';
import React, { Component, ComponentType } from 'react';
import { get, isNumber } from 'lodash';
import { SearchRecordBy } from '../Types';
import { insert, updateArray, removeAt } from '../../utility/Util';
import {
  VIRTUAL_SCROLL_PROPS, 
  VIRTUAL_SCROLL_STATE,
  INSERT_RECORDS_PARAMS
} from './Types';

function virtualScroll<P>(GridComponent: ComponentType<P>, edit?: boolean) {
  return class extends Component<P, VIRTUAL_SCROLL_STATE> {
    static displayName: string = 'VirtualScrollHOC';

    bufferedScrollDelay = 250;
    bufferedTimeout;
    gridRef: any;
    /**
     * Tree grid ref
     */
    wrapGridRef: any;
    isBufferedScroll = false;
    prevScrollTop = 0;

    state = {
      total: 0
    };
    insertRecordsParams: INSERT_RECORDS_PARAMS[] = [];
    enableInsertRecords: boolean = true;

    constructor(props: VIRTUAL_SCROLL_PROPS & P) {
      super(props);
      Object.assign(this, {
        gridRef: React.createRef(),
        isBufferedScroll: props.gridType === 'tree'
      });
    }

    componentWillUnmount() {
      window.clearTimeout(this.bufferedTimeout);
    }

    /**
     * Page event handler, set skip parameter
     */
    pageChange = (event: GridPageChangeEvent) => {
      const { scrollTop } = event?.nativeEvent?.target || {};

      // we should only load new data on vertical scroll
      if (!isNumber(scrollTop) || scrollTop !== this.prevScrollTop) {
        let { skip } = event.page;
        // after searching in the search bar of the Dashboard, the event returned a bad skip and attendedSkip (only if is gridType tree)
        if (scrollTop === 0 && skip !== 0) {
          skip = 0;
          this.gridRef.gridRef.vs.attendedSkip = 0;
        }
        const requestSkip = () => {
          skip = this.requestIfNeeded(skip);
          this.gridRef.setState({
            skip
          }, () => {
            if (this.props.onPageChange) {
              this.props.onPageChange(event);
            }
          });
        };

        if (this.isBufferedScroll) {
          this.gridRef.setState({ skip }, () => {
            clearTimeout(this.bufferedTimeout);
            this.bufferedTimeout = window.setTimeout(requestSkip, this.bufferedScrollDelay);
          });
        } else {
          requestSkip();
        }
      }

      this.prevScrollTop = scrollTop;
    };

    /**
     * Call request for new page of data if needed
     */
    requestIfNeeded = (skip: number): number => {
      const { pageSize } = this.gridRef.props;
      const { data, pageDataPopulated, pageNumber: lastPageNumber, total, addedTotal = 0 } = this.gridRef.state;
      const maxPageNumber: number = Math.ceil((total - addedTotal) / pageSize) - 1;
      let remoteIndex = 0;
      let isRemoteItem = false;
      for (let i = skip; i < skip + pageSize && i < data.length; i++) {
        isRemoteItem = _.isNumber(data[i].OriginalIndex);
        if (isRemoteItem) {
          remoteIndex = data[i].OriginalIndex;
        }
        if (isRemoteItem && !data[i].populated) {
          let pageNumber = Math.ceil((remoteIndex + pageSize) / pageSize) - 1;
          if (pageNumber > maxPageNumber) {
            pageNumber = maxPageNumber;
          }
          if (pageNumber === lastPageNumber && lastPageNumber === maxPageNumber) {
            pageNumber--; // load previous page when scrolling up from down
          }

          if (pageNumber < 0) {
            pageNumber = 0;
          }

          if (!pageDataPopulated.includes(pageNumber)) {
            this.requestData(pageNumber, skip);
          } else {
            // fix for scrolling up, don't allow to get stuck without data
            pageNumber--;

            if (pageNumber < 0) {
              pageNumber = 0;
            }
            if (!pageDataPopulated.includes(pageNumber)) {
              this.requestData(pageNumber, skip);
            }
          }
          break;
        }
      }
      return skip;
    };

    /**
     * Request new page of data
     */
    requestData = (pageNumber: number, skip: number) => {
      // request data only if not already fetched
      if (this.gridRef.state.requestInProgress || this.gridRef.props.requestInProgress) {
        // perform only one request at a time
        return;
      }
      let pageDataPopulated = this.gridRef.state.pageDataPopulated;
      pageDataPopulated.push(pageNumber);
      let nextState: any = {
        pageDataPopulated,
        pageNumber,
        skip,
        requestInProgress: true
      };

      this.gridRef.setState(nextState, () => {
        this.gridRef.refreshData({}, {
          scrollRequestParams: this.gridRef.prepareRequestParams(pageNumber)
        });
      });
    };

    /**
     * Sync request new page of data
     */
    syncRequestData = async (pageNumber: number, skip: number) => {
      // request data only if not already fetched
      if (this.gridRef.state.requestInProgress) {
        // perform only one request at a time
        return;
      }
      let pageDataPopulated = this.gridRef.state.pageDataPopulated;
      pageDataPopulated.push(pageNumber);
      let nextState: any = {
        pageDataPopulated,
        pageNumber,
        skip,
        requestInProgress: true
      };
      await this.gridRef.setStateSync(nextState);
      this.gridRef.refreshData({}, {
        scrollRequestParams: this.gridRef.prepareRequestParams(pageNumber)
      });
    };

    /**
     * Parse data and set it to grid
     */
    setGridData = (data: any, forceSkip?: boolean) => {
      let { items = [], gridContentInfo } = data;
      let { data: stateData, pageDataPopulated, pageNumber, skip, tasks = {}, total = 0, addedTotal = 0 } = this.gridRef.state;
      const { pageSize } = this.gridRef.props;
      let newStateObj: any = {
        dataLoadDone: true,
        requestInProgress: false,
        total
      };

      if (items.length) {
        total = gridContentInfo.totalCount;
        let indexForAdd = pageSize * pageNumber;
        let allData;
        let dataChanged = stateData.length !== total + addedTotal;
        if (!dataChanged) {
          allData = stateData.slice();
        } else {
          pageDataPopulated = [];
          allData = new Array(total).fill(undefined, 0, total).map((e: any, i: number) => ({
            Index: i,
            OriginalIndex: i
          }));
        }
        let pagedListInfo = get(data.gridContentInfo, 'pagedListInfo');
        let responsePageIndex = pagedListInfo?.index;
        if (pagedListInfo) {
          let tasksForPage = data.tasks;
          if (tasksForPage?.length) {
            tasks[responsePageIndex] = tasksForPage;
          }
        }

        if (isNumber(responsePageIndex) && responsePageIndex !== 0) {
          indexForAdd = responsePageIndex * pageSize;
        }
        let index: number = indexForAdd;

        let realIndex = -1;
        if (addedTotal) {
          realIndex = allData.findIndex((allDataItem: any) => allDataItem.OriginalIndex === index);
          indexForAdd = realIndex;
        }

        items.forEach((item: any) => {
          if (isNumber(responsePageIndex)) {
            item.pageIndex = responsePageIndex;
          }

          if (realIndex === -1) {
            allData[index] = {
              ...allData[index],
              Index: index,
              ...item,
              populated: true
            };
            allData[index].tasks = Array.isArray(get(tasks, responsePageIndex)) ? tasks[responsePageIndex] : [];
            index++;
          } else {
            for (realIndex; realIndex < allData.length; realIndex++) {
              if (allData[realIndex].OriginalIndex === index) {
                allData[realIndex] = {
                  ...allData[realIndex],
                  ...item,
                  populated: true
                };
                allData[realIndex].tasks = Array.isArray(get(tasks, responsePageIndex)) ? tasks[responsePageIndex] : [];
                index++;
                break;
              }
            }
          }
        });
        total += addedTotal;
        let realSkip: number = (total < indexForAdd + pageSize) ? total - pageSize : skip;
        if (index >= total) {
          realSkip = skip;
        }
        if (realSkip < 0) {
          realSkip = 0;
        }

        if (!dataChanged && realSkip < skip) {
          realSkip = skip;
        }
        let skipDifference: number = realSkip - skip;
        if (skipDifference > pageSize) {
          realSkip = skip + (skipDifference - pageSize);
        }

        if (pageDataPopulated.length === 0 && !forceSkip) {
          realSkip = 0;
        }

        Object.assign(newStateObj, {
          data: allData,
          pageDataPopulated,
          requestInProgress: false,
          skip: realSkip,
          total: total
        });
        if (tasks) {
          Object.assign(newStateObj, { tasks });
        }
      }
      if (total !== this.state.total) {
        this.setState({
          total
        }, () => {
          this.gridRef.setState({ ...newStateObj }, () => {
            this.insertRecordsAfterFirstLoad();
          });
        });
      } else {
        this.gridRef.setState(newStateObj, () => {
          this.insertRecordsAfterFirstLoad();
        });
      }
      this.requestIfNeeded(skip);
    };

    /**
     * Executes insertRecords method after first load
     */
    insertRecordsAfterFirstLoad = () => {
      if (this.insertRecordsParams.length) {
        const items = [...this.insertRecordsParams];
        this.insertRecordsParams = [];
        this.insertRecords(items);
      }
    }

    /**
     * Returns row record based on search param there can be value of record uniqueField or record index. It depends on searchBy param.
     * @param {String|Number} search Value of record uniqueField param or record index.
     * @param {String} searchBy There can be 'uniqueField' or 'index' (SearchRecordBy.uniqueField or  SearchRecordBy.index). Default value is 'uniqueField' (SearchRecordBy.uniqueField)
     **/
    getRecord = (search: any, searchBy: SearchRecordBy = SearchRecordBy.uniqueField) => {
      const dataState = this.gridRef.getDataState();
      const { uniqueField } = this.gridRef.props;
      let record = null;
      let index: number = -1;
      switch (searchBy) {
        case SearchRecordBy.uniqueField:
          index = dataState.findIndex(record => record[uniqueField] === search);
          break;
        case SearchRecordBy.index:
          index = search;
          break;
        case SearchRecordBy.selected:
          let { lastClickedIndex } = this.gridRef;
          index = lastClickedIndex;
          break;
        default:
          break;
      }
      if (index !== -1) {
        record = dataState[index];
      }
      return record;
    };

    /**
     * Returns record index in table based on record object
     * @param {Object} item Row record object
     */
    getRecordIndex = (item: any) => {
      const dataState = this.gridRef.getDataState();
      const { uniqueField } = this.gridRef.props;
      return dataState.findIndex(record => record[uniqueField] === item[uniqueField]);
    };

    /**
     * getRecordByIndex for fetching data item by index
     * @param index {number}
     */
    getRecordByIndex = (index: number) => this.gridRef.getDataState()[index];

    /**
     * Returns table data that should be updated based on record object
     * @param {Object} item Row record object
     * @param {Boolean} populated If true record param populated will be true
     */
    getUpdateRecordData = (item: any, populated: boolean = true) => {
      item = {
        ...item,
        populated
      };
      const dataState = this.gridRef.getDataState().slice();
      const { uniqueField } = this.gridRef.props;
      const index = dataState.findIndex(record => record[uniqueField] === item[uniqueField]);
      dataState[index] = {
        ...dataState[index],
        ...item
      };
      return dataState;
    };

    /**
     * Inserts new rows by client side to one position (Don't save on server side)
     * @param  {INSERT_RECORDS_PARAMS[]} insertRecordsToMultiplePositions
     */
    insertRecords = (insertRecordsToMultiplePositions: INSERT_RECORDS_PARAMS[]) => {
      const dataState = this.gridRef.getDataState();
      let addedTotal = this.gridRef.state.addedTotal;
      if (dataState.length && this.enableInsertRecords) {
        this.enableInsertRecords = false;
        insertRecordsToMultiplePositions.forEach(({ records, index }) => {
          if (dataState.length >= index) {
            records = records.map((item, ind) => {
              item = {
                ...item,
                Index: index + ind,
                populated: true
              };
              return item;
            });
            insert(dataState, index, records);
            for (let restMemberIndex = index + records.length; restMemberIndex < dataState.length; restMemberIndex++) {
              dataState[restMemberIndex].Index = restMemberIndex;
            }
            this.gridRef.scrollCorrectionServiceVS.setNewClientRows(index, records.length);
            addedTotal += records.length;
          }
        });
        this.gridRef.setState({
          addedTotal,
          dataLoadDone: false,
          data: [...dataState],
          total: dataState.length
        }, () => {
          this.enableInsertRecords = true;
          this.gridRef.scrollCorrectionServiceVS.setFixedScroll(false);
          this.insertRecordsAfterFirstLoad();
        });
      } else {
        this.insertRecordsParams = _.union(this.insertRecordsParams, insertRecordsToMultiplePositions);
      }
    }

    /**
     * Updates table data based on record object
     * @param {Object} item Row record object
     * @param {Boolean} populated If true record param populated will be true
     */
    updateRecord = (item: any, populated: boolean = true) => {
      const dataState = this.getUpdateRecordData(item, populated);
      this.gridRef.setState({ data: dataState });
    };

    /**
     * Updates table data based on record objects
     * @param {Object[]} items Row record object
     * @param {Boolean} populated If true records param populated will be true
     */
    updateRecords = (items: any[], populated: boolean = true) => {
      let dataState = this.gridRef.getDataState();
      items.forEach((item) => {
        dataState = this.getUpdateRecordData(item, populated);
      });
      this.gridRef.setState({ data: dataState });
    };

    /**
     * Updates table data based on record objects and index of start record
     * @param {Object[]} items Row record object
     * @param {Number} index Index of start record
     * @param {Boolean} populated If true records param populated will be true
     */
    updateRecordsByIndex = (items: any[], index: number, populated: boolean = true) => {
      if (populated) {
        items = items.map((item) => {
          item.populated = populated;
          return item;
        });
      }
      let dataState = this.gridRef.getDataState();
      dataState = updateArray(dataState, items, index);
      this.gridRef.setState({ data: dataState });
    };

    VIRTUAL_SCROLL = {
      isVirtualScrollHOC: true,
      onPageChange: this.pageChange,
      requestData: this.requestData,
      syncRequestData: this.syncRequestData,
      setGridData: this.setGridData,
      requestIfNeeded: this.requestIfNeeded,
      getRecord: this.getRecord,
      getRecordIndex: this.getRecordIndex,
      updateRecord: this.updateRecord,
      updateRecords: this.updateRecords,
      updateRecordsByIndex: this.updateRecordsByIndex,
      getRecordByIndex: this.getRecordByIndex,
      insertRecords: this.insertRecords
    };

    /**
     * Reference callback
     */
    refCallback = (ref: any) => {
      this.wrapGridRef = ref;
      this.gridRef = edit && ref && ref.gridRef ? ref.gridRef : ref;

      if (this.gridRef) {
        if (!this.gridRef.props.requestActionName) {
          throw new Error('requestActionName need to be passed as props to NavigationGrid Component');
        }

        if (!this.gridRef.prepareRequestParams) {
          throw new Error('prepareRequestParams Function need to be passed as props to NavigationGrid Component');
        }

        if (!this.gridRef.props.excludeFirstRequest) {
          this.requestData(0, 0);
        }
      }
    };

    render() {
      const total = this.state.total || this.props.total;
      return (
        <GridComponent
          {...this.props}
          columnVirtualisation={true}
          ref={this.refCallback}
          scrollable={'virtual'}
          {...this.VIRTUAL_SCROLL}
          total={total}
        />
      );
    }
  };
}

export default virtualScroll;
